// Assets API service
import { apiService } from './api';
import {
  Asset,
  AssetChoice,
  AssetCreate,
  AssetUpdate,
  AssetFilters,
  AssetSearchParams,
  AssetSearchResult,
  AssetCloneRequest,
  AssetCloneResponse,
  Checkpoint,
  QuickAddAssetItem,
} from '../types';

export type AssetFileKind = 'photo' | 'attachment';

export interface AssetFile {
  id: string;
  asset_id: string;
  filename: string;
  content_type: string;
  storage_key: string;
  kind: AssetFileKind;
  size_bytes: number | null;
  uploaded_by_id: string | null;
  created_at: string;
}

export class AssetsService {
  async getAssetChoices(facilityId: string): Promise<AssetChoice[]> {
    return apiService.get<AssetChoice[]>('/assets/choices', { facility_id: facilityId });
  }

  async getAssets(params?: AssetFilters): Promise<Asset[]> {
    return apiService.get<Asset[]>('/assets/', params);
  }

  async getAssetById(id: string): Promise<Asset> {
    return apiService.get<Asset>(`/assets/${id}`);
  }

  async createAsset(assetData: AssetCreate): Promise<Asset> {
    return apiService.post<Asset>('/assets/', assetData);
  }

  async updateAsset(id: string, assetData: AssetUpdate): Promise<Asset> {
    return apiService.put<Asset>(`/assets/${id}`, assetData);
  }

  async deleteAsset(id: string): Promise<void> {
    return apiService.delete<void>(`/assets/${id}`);
  }

  async getAssetChildren(id: string): Promise<Asset[]> {
    return apiService.get<Asset[]>(`/assets/${id}/children`);
  }

  async getAssetCheckpoints(id: string): Promise<Checkpoint[]> {
    return apiService.get<Checkpoint[]>(`/assets/${id}/checkpoints`);
  }

  async getAssetHierarchy(id: string): Promise<Asset[]> {
    return apiService.get<Asset[]>(`/assets/${id}/hierarchy`);
  }

  async searchAssets(params: AssetSearchParams): Promise<AssetSearchResult[]> {
    return apiService.get<AssetSearchResult[]>('/assets/search', params);
  }

  async getQuickAddCatalog(): Promise<QuickAddAssetItem[]> {
    return apiService.get<QuickAddAssetItem[]>('/assets/quick-add-catalog');
  }

  async cloneAsset(id: string, payload: AssetCloneRequest): Promise<AssetCloneResponse> {
    return apiService.post<AssetCloneResponse>(`/assets/${id}/clone`, payload);
  }

  async uploadAssetFile(
    assetId: string,
    file: File,
    kind: AssetFileKind
  ): Promise<AssetFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    const response = await fetch(
      `${apiService.getBaseUrl()}/assets/${assetId}/files`,
      {
        method: 'POST',
        headers: await apiService.authHeaders(),
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to upload asset file: ${response.status}`);
    }
    return response.json();
  }

  async listAssetFiles(assetId: string): Promise<AssetFile[]> {
    return apiService.get<AssetFile[]>(`/assets/${assetId}/files`);
  }

  assetFileDownloadUrl(assetId: string, fileId: string): string {
    return `${apiService.getBaseUrl()}/assets/${assetId}/files/${fileId}`;
  }

  async downloadAssetFile(assetId: string, fileId: string): Promise<Blob> {
    const response = await fetch(this.assetFileDownloadUrl(assetId, fileId), {
      headers: await apiService.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to download asset file: ${response.status}`);
    }
    return response.blob();
  }

  async deleteAssetFile(assetId: string, fileId: string): Promise<void> {
    return apiService.delete<void>(`/assets/${assetId}/files/${fileId}`);
  }
}

export const assetsService = new AssetsService();
